import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import Seo from "@/shop/Seo";
import { apiGet } from "@/shop/api";
import ProductCard from "@/components/shop/ProductCard";

const SORTS = [
  { id: "featured", label: "Selezione Stillwater" },
  { id: "newest", label: "Più recenti" },
  { id: "price_asc", label: "Prezzo crescente" },
  { id: "price_desc", label: "Prezzo decrescente" },
];

export default function TackleList() {
  const [params, setParams] = useSearchParams();
  const brand = params.get("brand") || "";
  const category = params.get("tipo") || "";
  const availability = params.get("disponibilita") || "";
  const sort = params.get("ordina") || "featured";

  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const qs = new URLSearchParams({ product_type: "selected_tackle", sort });
    if (brand) qs.set("brand", brand);
    if (category) qs.set("category", category);
    if (availability) qs.set("availability", availability);
    apiGet(`/api/shop/products?${qs}`)
      .then(setProducts)
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [brand, category, availability, sort]);

  useEffect(() => {
    apiGet("/api/shop/brands?product_type=selected_tackle").then(setBrands).catch(() => {});
    apiGet("/api/shop/categories?product_type=selected_tackle").then(setCategories).catch(() => {});
  }, []);

  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v);
    else next.delete(k);
    setParams(next, { replace: true });
  };

  const activeFilters = (brand ? 1 : 0) + (category ? 1 : 0) + (availability ? 1 : 0);

  const Filters = useMemo(
    () => (
      <div className="space-y-6" data-testid="shop-filters">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-faint mb-3">Marca</p>
          <div className="flex flex-wrap gap-2">
            <button
              data-testid="filter-brand-all"
              onClick={() => setParam("brand", "")}
              className={`font-mono text-[10px] uppercase tracking-[0.2em] px-3.5 py-1.5 rounded-full border transition-colors ${!brand ? "border-ember text-ember" : "border-ink-border text-mist hover:text-bone"}`}
            >
              Tutte
            </button>
            {brands.map((b) => (
              <button
                key={b.slug}
                data-testid={`filter-brand-${b.slug}`}
                onClick={() => setParam("brand", b.slug === brand ? "" : b.slug)}
                className={`font-mono text-[10px] uppercase tracking-[0.2em] px-3.5 py-1.5 rounded-full border transition-colors ${brand === b.slug ? "border-ember text-ember" : "border-ink-border text-mist hover:text-bone"}`}
              >
                {b.name}
              </button>
            ))}
          </div>
        </div>
        {categories.length > 0 && (
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-faint mb-3">Tipo</p>
            <div className="flex flex-wrap gap-2">
              {categories.map((c) => (
                <button
                  key={c}
                  data-testid={`filter-type-${c}`}
                  onClick={() => setParam("tipo", c === category ? "" : c)}
                  className={`font-mono text-[10px] uppercase tracking-[0.2em] px-3.5 py-1.5 rounded-full border transition-colors ${category === c ? "border-ember text-ember" : "border-ink-border text-mist hover:text-bone"}`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
        )}
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-faint mb-3">
            Disponibilità
          </p>
          <div className="flex flex-wrap gap-2">
            {[
              { id: "", label: "Tutto" },
              { id: "in", label: "Disponibile" },
            ].map((a) => (
              <button
                key={a.id}
                data-testid={`filter-avail-${a.id || "all"}`}
                onClick={() => setParam("disponibilita", a.id)}
                className={`font-mono text-[10px] uppercase tracking-[0.2em] px-3.5 py-1.5 rounded-full border transition-colors ${availability === a.id ? "border-ember text-ember" : "border-ink-border text-mist hover:text-bone"}`}
              >
                {a.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    ),
    [brands, categories, brand, category, availability],
  );

  return (
    <div className="bg-ink text-bone min-h-screen" data-testid="tackle-list">
      <Seo
        title="Selected Tackle — Stillwater Shop"
        description="Attrezzatura moderna selezionata dalla redazione Stillwater. Pochi pezzi, scelti come si scelgono le storie da raccontare."
        canonical="https://stillwatermag.eu/shop/selected-tackle"
      />
      <header className="px-5 md:px-10 pt-28 md:pt-36 pb-10 md:pb-14 border-b border-ink-border">
        <p className="font-mono text-[10px] md:text-xs uppercase tracking-[0.35em] text-ember mb-4">
          Shop — Selezione
        </p>
        <h1 className="font-serif font-light text-4xl md:text-6xl tracking-tight">
          Selected <span className="italic text-ember">tackle</span>
        </h1>
        <p className="mt-4 max-w-xl text-sm md:text-base text-mist leading-relaxed">
          Circa 30–40 referenze per volta: il massimo della selezione, non il
          catalogo completo del produttore.
        </p>
      </header>

      <div className="px-5 md:px-10 py-8 flex flex-col lg:flex-row-reverse gap-10">
        <aside className="lg:w-72 shrink-0">
          <div className="hidden lg:block sticky top-28">{Filters}</div>
          <details className="lg:hidden border border-ink-border rounded-lg" data-testid="filters-mobile">
            <summary className="flex items-center justify-between cursor-pointer font-mono text-[10px] uppercase tracking-[0.3em] text-mist px-4 py-3.5">
              Filtri {activeFilters > 0 && `(attivi: ${activeFilters})`}
              <ChevronDown size={14} strokeWidth={1.5} />
            </summary>
            <div className="px-4 pb-5 pt-1 border-t border-ink-border mt-1">{Filters}</div>
          </details>
        </aside>

        <main className="flex-1">
          <div className="flex items-center justify-between mb-6">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-faint" data-testid="products-count">
              {loading ? "…" : `${products.length} oggetti`}
            </p>
            <select
              data-testid="sort-select"
              value={sort}
              onChange={(e) => setParam("ordina", e.target.value === "featured" ? "" : e.target.value)}
              className="bg-ink border border-ink-border rounded-full px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-mist focus:outline-none focus:border-ember"
            >
              {SORTS.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>

          {!loading && products.length === 0 && (
            <div className="border border-ink-border bg-ink-surface px-6 py-16 text-center" data-testid="empty-state">
              <p className="font-serif italic text-2xl text-bone">La selezione è in allestimento.</p>
              <p className="mt-3 text-sm text-mist">
                Stiamo scegliendo le prime referenze con la calma di chi pesca
                di notte. Torna a galla tra poco.
              </p>
            </div>
          )}

          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 md:gap-7">
            {products.map((p, i) => (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: (i % 6) * 0.06, ease: [0.16, 1, 0.3, 1] }}
              >
                <ProductCard p={p} />
              </motion.div>
            ))}
          </div>
        </main>
      </div>
    </div>
  );
}
